import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import { MapContainer, TileLayer, LayersControl, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import 'leaflet.markercluster';
import 'leaflet.markercluster/dist/MarkerCluster.css';
import 'leaflet.markercluster/dist/MarkerCluster.Default.css';
import { BASE_LAYERS } from '../../utils/basemaps';
import './CRMDashboardMap.css';

const DEFAULT_CENTER = [20, 0];
const DEFAULT_ZOOM = 2;

// ── Approval-status visual config — pins are coloured by approval status so
// the whole partner network reads at a glance on the dashboard map. Each entry
// also carries a gradient (top/bottom) so the pins match the richer teardrop
// style used on the partner-profile Address Map. ─────────────────────────────
const STATUS_CONFIG = {
  approved: { label: 'Approved', color: '#16a34a', top: '#4ade80', bottom: '#15803d' },
  pending:  { label: 'Pending',  color: '#f59e0b', top: '#fcd34d', bottom: '#b45309' },
  rejected: { label: 'Rejected', color: '#dc2626', top: '#f87171', bottom: '#b91c1c' },
};
const STATUS_FALLBACK = { label: 'Unknown', color: '#64748b', top: '#94a3b8', bottom: '#475569' };
const statusOf = (s) => STATUS_CONFIG[s] || STATUS_FALLBACK;

const STATUS_FILTERS = [
  { key: 'all',      label: 'All' },
  { key: 'approved', label: 'Approved' },
  { key: 'pending',  label: 'Pending' },
  { key: 'rejected', label: 'Rejected' },
];

const TYPE_FILTERS = [
  { key: 'all',      label: 'All' },
  { key: 'debtor',   label: 'Debtor' },
  { key: 'creditor', label: 'Creditor' },
  { key: 'both',     label: 'Both' },
];

// ── Helpers ──────────────────────────────────────────────────────────────────
const escapeHtml = (value) => String(value ?? '').replace(/[&<>"']/g, (ch) => (
  { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[ch]
));

const hasType = (selections, needle) =>
  Array.isArray(selections) && selections.some(t => String(t).toLowerCase().includes(needle));

const typeMatches = (pin, filter) => {
  if (filter === 'all') return true;
  const sel = pin.partner_type_selections;
  if (filter === 'debtor')   return hasType(sel, 'debtor');
  if (filter === 'creditor') return hasType(sel, 'creditor');
  if (filter === 'both')     return hasType(sel, 'both') || (hasType(sel, 'debtor') && hasType(sel, 'creditor'));
  return true;
};

// ── Pin icon — gradient teardrop coloured by approval status, with a soft drop
// shadow and a status-coloured centre dot. The selected pin renders larger with
// a pulsing ring so it stands out as the radius anchor. ───────────────────────
const buildPinIcon = (status, id, highlighted = false) => {
  const { color, top, bottom } = statusOf(status);
  const gId = `cdm-g-${String(id).replace(/[^a-z0-9-]/gi, '')}`;
  const sId = `cdm-s-${String(id).replace(/[^a-z0-9-]/gi, '')}`;
  const w = highlighted ? 38 : 28;
  const h = highlighted ? 52 : 38;
  const ring = highlighted
    ? `<circle cx="18" cy="15.5" r="12.5" fill="none" stroke="${color}" stroke-width="2" opacity="0.4" class="crm-dashboard-map-pin-ring"/>`
    : '';
  const html = `
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 36 48" width="${w}" height="${h}" aria-hidden="true">
      <defs>
        <linearGradient id="${gId}" x1="18" y1="2" x2="18" y2="44" gradientUnits="userSpaceOnUse">
          <stop offset="0%" stop-color="${top}"/>
          <stop offset="60%" stop-color="${bottom}"/>
          <stop offset="100%" stop-color="${bottom}"/>
        </linearGradient>
        <filter id="${sId}" x="-30%" y="-20%" width="160%" height="160%">
          <feDropShadow dx="0" dy="2" stdDeviation="2" flood-color="#1e293b" flood-opacity="0.28"/>
        </filter>
      </defs>
      <ellipse cx="18" cy="45.5" rx="6.5" ry="2" fill="rgba(15,23,42,0.16)"/>
      <path filter="url(#${sId})"
        d="M18 2.5C11.1 2.5 5.5 8.1 5.5 15c0 8.6 12.5 27.5 12.5 27.5S30.5 23.6 30.5 15C30.5 8.1 24.9 2.5 18 2.5z"
        fill="url(#${gId})" stroke="#fff" stroke-width="2" stroke-linejoin="round"/>
      <circle cx="18" cy="15.5" r="8" fill="#fff"/>
      <circle cx="18" cy="15.5" r="4" fill="${color}"/>
      ${ring}
    </svg>`;
  return L.divIcon({
    html,
    className: `crm-dashboard-map-pin${highlighted ? ' crm-dashboard-map-pin--selected' : ''}`,
    iconSize: [w, h],
    iconAnchor: [w / 2, h - 1],
    popupAnchor: [0, -(h - 8)],
  });
};

// Hover card — partner basics + a reachable "View Profile" button. Rendered as
// a Leaflet popup (interactive) opened on hover, so the user can move the mouse
// from the pin onto the card to click the button.
const hoverCardHtml = (pin) => {
  const { label, color } = statusOf(pin.approval_status);
  const trading = pin.trading_short_name
    ? `<div class="crm-dashboard-map-popup-trading">${escapeHtml(pin.trading_short_name)}</div>` : '';
  const types = (pin.partner_type_selections && pin.partner_type_selections.length)
    ? `<div class="crm-dashboard-map-popup-types">${pin.partner_type_selections.map(t =>
        `<span class="crm-dashboard-map-popup-chip">${escapeHtml(t)}</span>`).join('')}</div>` : '';
  return `
    <div class="crm-dashboard-map-popup">
      <div class="crm-dashboard-map-popup-name">${escapeHtml(pin.legal_entity_name || 'Unnamed partner')}</div>
      ${trading}
      <span class="crm-dashboard-map-popup-badge" style="background:${color}">${escapeHtml(label)}</span>
      ${types}
      <button type="button" class="crm-dashboard-map-popup-btn">View Profile</button>
    </div>`;
};

// ── Cluster layer — manages an L.markerClusterGroup imperatively. Markers show
// a hoverable card (popup opened on mouseover, kept open while the cursor is
// over the card) and click-to-select for the radius anchor. ───────────────────
const ClusterLayer = ({ pins, selectedId, onSelect, onViewProfile, fitTrigger }) => {
  const map = useMap();
  const groupRef = useRef(null);
  const markersByIdRef = useRef(new Map());
  const hasFittedRef = useRef(false);
  const closeTimerRef = useRef(null);
  const prevSelectedRef = useRef(null);
  const onSelectRef = useRef(onSelect);
  const onViewProfileRef = useRef(onViewProfile);
  const selectedIdRef = useRef(selectedId);
  onSelectRef.current = onSelect;
  onViewProfileRef.current = onViewProfile;
  selectedIdRef.current = selectedId;

  // Create the cluster group once and attach to the map.
  useEffect(() => {
    const group = L.markerClusterGroup({
      chunkedLoading: true,
      showCoverageOnHover: false,
      maxClusterRadius: 50,
      spiderfyOnMaxZoom: true,
    });
    map.addLayer(group);
    groupRef.current = group;
    return () => {
      map.removeLayer(group);
      groupRef.current = null;
    };
  }, [map]);

  // Rebuild markers when the filtered pin set changes.
  useEffect(() => {
    const group = groupRef.current;
    if (!group) return;
    group.clearLayers();
    const byId = new Map();

    const markers = pins.map((pin) => {
      const hi = pin.id === selectedIdRef.current;
      const marker = L.marker([pin.lat, pin.lon], { icon: buildPinIcon(pin.approval_status, pin.id, hi) });
      marker._pin = pin;

      marker.bindPopup(hoverCardHtml(pin), {
        closeButton: false,
        autoPan: false,
        closeOnClick: false, // a stray map click shouldn't kill it mid-interaction
        offset: [0, -2],
        className: 'crm-dashboard-map-hovercard',
      });
      // Replace Leaflet's default click-to-open-popup with click-to-select.
      marker.off('click');
      marker.on('click', () => onSelectRef.current(pin));
      // Hover opens the card; leaving starts a short close timer that the card's
      // own mouseenter cancels — letting the cursor travel from pin to button.
      marker.on('mouseover', () => { clearTimeout(closeTimerRef.current); marker.openPopup(); });
      marker.on('mouseout', () => {
        closeTimerRef.current = setTimeout(() => marker.closePopup(), 350);
      });
      marker.on('popupopen', (e) => {
        const el = e.popup.getElement();
        if (!el) return;
        el.addEventListener('mouseenter', () => clearTimeout(closeTimerRef.current));
        el.addEventListener('mouseleave', () => {
          closeTimerRef.current = setTimeout(() => marker.closePopup(), 160);
        });
        const btn = el.querySelector('.crm-dashboard-map-popup-btn');
        if (btn) {
          // Use Leaflet's DOM helpers so the click can't be swallowed by the
          // map/popup layer (L.DomEvent.stop = preventDefault + stopPropagation).
          L.DomEvent.on(btn, 'mousedown click', L.DomEvent.stop);
          L.DomEvent.on(btn, 'click', () => {
            clearTimeout(closeTimerRef.current);
            onViewProfileRef.current(pin.id);
          });
        }
      });

      byId.set(pin.id, marker);
      return marker;
    });
    markersByIdRef.current = byId;
    prevSelectedRef.current = selectedIdRef.current;
    group.addLayers(markers);

    // Auto-fit to all pins on the first non-empty render only.
    if (!hasFittedRef.current && pins.length > 0) {
      hasFittedRef.current = true;
      const bounds = group.getBounds();
      if (bounds.isValid()) map.fitBounds(bounds, { padding: [50, 50], maxZoom: 12 });
    }
  }, [pins, map]);

  // Re-icon only the previously- and newly-selected markers when selection
  // changes (avoids rebuilding hundreds of markers on every click).
  useEffect(() => {
    const byId = markersByIdRef.current;
    const prev = prevSelectedRef.current;
    if (prev && prev !== selectedId && byId.has(prev)) {
      const m = byId.get(prev);
      m.setIcon(buildPinIcon(m._pin.approval_status, m._pin.id, false));
    }
    if (selectedId && byId.has(selectedId)) {
      const m = byId.get(selectedId);
      m.setIcon(buildPinIcon(m._pin.approval_status, m._pin.id, true));
    }
    prevSelectedRef.current = selectedId;
  }, [selectedId]);

  // Manual "Fit all".
  useEffect(() => {
    if (!fitTrigger) return;
    const group = groupRef.current;
    if (!group) return;
    const bounds = group.getBounds();
    if (bounds.isValid()) map.fitBounds(bounds, { padding: [50, 50] });
  }, [fitTrigger, map]);

  return null;
};

// ── Radius layer — draws the service-radius ring centred on the SELECTED pin. ─
const RadiusLayer = ({ center, radiusKm }) => {
  const map = useMap();
  const ref = useRef(null);
  useEffect(() => {
    if (ref.current) { map.removeLayer(ref.current); ref.current = null; }
    if (!center) return;
    const circle = L.circle(center, {
      radius: radiusKm * 1000,
      interactive: false,
      color: '#3a5b15',
      fillColor: '#3a5b15',
      fillOpacity: 0.07,
      weight: 1.5,
      dashArray: '6 4',
    });
    circle.addTo(map);
    ref.current = circle;
    return () => { if (ref.current) { map.removeLayer(ref.current); ref.current = null; } };
  }, [map, center, radiusKm]);
  return null;
};

// ── Pan helper — eases the map to a pin when it's selected from the infobar,
// so "select on the map" is actually visible even if the pin was off-screen. ──
const PanToPin = ({ target }) => {
  const map = useMap();
  const lastSeq = useRef(0);
  useEffect(() => {
    if (!target || target.seq === lastSeq.current) return;
    lastSeq.current = target.seq;
    map.panTo([target.lat, target.lon], { animate: true, duration: 0.6 });
  }, [target, map]);
  return null;
};

// ``pins``, ``loading`` and ``error`` are owned by CRMDashboard.
const CRMDashboardMap = ({ pins = [], loading = false, error = null }) => {
  const history = useHistory();
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [fitTrigger, setFitTrigger] = useState(0);
  const [selectedId, setSelectedId] = useState(null);
  const [showRadius, setShowRadius] = useState(false);
  const [radiusKm, setRadiusKm] = useState(500);
  const [debouncedRadiusKm, setDebouncedRadiusKm] = useState(500);
  const [panTo, setPanTo] = useState(null); // {lat, lon, seq} — set when selecting via the infobar
  const panSeqRef = useRef(0);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim().toLowerCase()), 250);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedRadiusKm(radiusKm), 400);
    return () => clearTimeout(t);
  }, [radiusKm]);

  const filteredPins = useMemo(() => pins.filter((pin) => {
    if (statusFilter !== 'all' && pin.approval_status !== statusFilter) return false;
    if (!typeMatches(pin, typeFilter)) return false;
    if (debouncedSearch) {
      const haystack = `${pin.legal_entity_name || ''} ${pin.trading_short_name || ''}`.toLowerCase();
      if (!haystack.includes(debouncedSearch)) return false;
    }
    return true;
  }), [pins, statusFilter, typeFilter, debouncedSearch]);

  const selectedPin = useMemo(
    () => filteredPins.find(p => p.id === selectedId) || null,
    [filteredPins, selectedId]
  );

  // If the selected pin gets filtered out, drop the selection (and the radius,
  // which has nothing to anchor to anymore).
  useEffect(() => {
    if (selectedId && !selectedPin) {
      setSelectedId(null);
      setShowRadius(false);
    }
  }, [selectedId, selectedPin]);

  const radiusCenter = useMemo(
    () => (showRadius && selectedPin ? [selectedPin.lat, selectedPin.lon] : null),
    [showRadius, selectedPin]
  );

  // Partners (other than the selected one) that fall inside the radius.
  const withinList = useMemo(() => {
    if (!showRadius || !selectedPin) return [];
    const origin = L.latLng(selectedPin.lat, selectedPin.lon);
    const metres = debouncedRadiusKm * 1000;
    return filteredPins
      .filter(p => p.id !== selectedPin.id && origin.distanceTo([p.lat, p.lon]) <= metres)
      .map(p => ({ ...p, distanceKm: origin.distanceTo([p.lat, p.lon]) / 1000 }))
      .sort((a, b) => a.distanceKm - b.distanceKm);
  }, [showRadius, selectedPin, debouncedRadiusKm, filteredPins]);

  const handleSelect = useCallback((pin) => {
    setSelectedId(prev => {
      if (prev === pin.id) { setShowRadius(false); return null; } // toggle off
      return pin.id;
    });
  }, []);

  const handleClearSelection = useCallback(() => {
    setSelectedId(null);
    setShowRadius(false);
  }, []);

  // Clicking a partner in the radius infobar makes IT the selected pin on the
  // map (re-anchoring the radius) and pans to it — it does not navigate away.
  const handleSelectFromInfobar = useCallback((pin) => {
    setSelectedId(pin.id);
    panSeqRef.current += 1;
    setPanTo({ lat: pin.lat, lon: pin.lon, seq: panSeqRef.current });
  }, []);

  const handleViewProfile = useCallback((id) => {
    history.push(`/dashboard/crm/profile/${id}`);
  }, [history]);

  return (
    <div className="crm-dashboard-map-section">
      <div className="crm-dashboard-map-toolbar">
        <div className="crm-dashboard-map-toolbar-left">
          <div className="crm-dashboard-map-filter-group">
            <span className="crm-dashboard-map-filter-label">Status</span>
            <div className="crm-dashboard-map-segmented">
              {STATUS_FILTERS.map(f => (
                <button key={f.key} type="button"
                  className={`crm-dashboard-map-seg-btn${statusFilter === f.key ? ' is-active' : ''}`}
                  onClick={() => setStatusFilter(f.key)}>{f.label}</button>
              ))}
            </div>
          </div>

          <div className="crm-dashboard-map-filter-group">
            <span className="crm-dashboard-map-filter-label">Type</span>
            <div className="crm-dashboard-map-segmented">
              {TYPE_FILTERS.map(f => (
                <button key={f.key} type="button"
                  className={`crm-dashboard-map-seg-btn${typeFilter === f.key ? ' is-active' : ''}`}
                  onClick={() => setTypeFilter(f.key)}>{f.label}</button>
              ))}
            </div>
            <input
              type="text"
              className="crm-dashboard-map-search"
              placeholder="Filter pins by name…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>

        <div className="crm-dashboard-map-toolbar-right">
          <label
            className={`crm-dashboard-map-radius-toggle${!selectedPin ? ' is-disabled' : ''}`}
            title={selectedPin
              ? 'Show a service-radius ring around the selected partner'
              : 'Click a partner pin on the map first, then enable the radius'}
          >
            <span className="crm-dashboard-map-radius-checkbox-wrap">
              <input type="checkbox" checked={showRadius} disabled={!selectedPin}
                onChange={e => setShowRadius(e.target.checked)} />
              <span className="crm-dashboard-map-radius-checkbox" aria-hidden="true">
                {showRadius && (
                  <svg viewBox="0 0 10 8" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M1 4L3.8 7L9 1" stroke="#3a5b15" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                )}
              </span>
            </span>
            <span>Radius</span>
            {showRadius && selectedPin && (
              <span className="crm-dashboard-map-radius-input-wrap">
                <input type="number" min="1" max="20000" step="50" value={radiusKm}
                  onChange={e => setRadiusKm(Math.max(1, Number(e.target.value)))}
                  className="crm-dashboard-map-radius-input"
                  onClick={e => e.stopPropagation()}
                />
                <span className="crm-dashboard-map-radius-unit">km</span>
              </span>
            )}
          </label>

          <span className="crm-dashboard-map-count">
            {loading ? 'Loading…' : `${filteredPins.length} of ${pins.length} shown`}
          </span>
          <button type="button" className="crm-dashboard-map-fit-btn"
            onClick={() => setFitTrigger(t => t + 1)} disabled={loading || filteredPins.length === 0}>
            Fit all
          </button>
        </div>
      </div>

      <div className="crm-dashboard-map-wrapper">
        {error ? (
          <div className="crm-dashboard-map-overlay crm-dashboard-map-overlay--error">{error}</div>
        ) : (
          <>
            {loading && (
              <div className="crm-dashboard-map-overlay crm-dashboard-map-overlay--loading">
                <span className="crm-dashboard-map-spinner" /> Loading partner locations…
              </div>
            )}
            {!loading && pins.length === 0 && (
              <div className="crm-dashboard-map-overlay crm-dashboard-map-overlay--empty">
                No geocoded partners yet. Add a Head Office address with coordinates to a partner profile to see it here.
              </div>
            )}
            {!loading && pins.length > 0 && filteredPins.length === 0 && (
              <div className="crm-dashboard-map-overlay crm-dashboard-map-overlay--empty">
                No partners match the current filters.
              </div>
            )}

            <MapContainer center={DEFAULT_CENTER} zoom={DEFAULT_ZOOM}
              className="crm-dashboard-map" scrollWheelZoom worldCopyJump>
              <LayersControl position="topright">
                {BASE_LAYERS.map((layer, i) => (
                  <LayersControl.BaseLayer key={layer.name} name={layer.name} checked={i === 0}>
                    <TileLayer attribution={layer.attribution} url={layer.url} />
                  </LayersControl.BaseLayer>
                ))}
              </LayersControl>

              {radiusCenter && <RadiusLayer center={radiusCenter} radiusKm={debouncedRadiusKm} />}
              <PanToPin target={panTo} />
              <ClusterLayer
                pins={filteredPins}
                selectedId={selectedId}
                onSelect={handleSelect}
                onViewProfile={handleViewProfile}
                fitTrigger={fitTrigger}
              />
            </MapContainer>

            {/* Radius infobar — partners inside the ring around the selected pin */}
            {showRadius && selectedPin && (
              <div className="crm-dashboard-map-infobar">
                <div className="crm-dashboard-map-infobar-head">
                  <div className="crm-dashboard-map-infobar-title">
                    <span className="crm-dashboard-map-infobar-anchor">
                      <span className="crm-dashboard-map-legend-dot"
                        style={{ background: statusOf(selectedPin.approval_status).color }} />
                      {selectedPin.legal_entity_name || 'Selected partner'}
                    </span>
                    <span className="crm-dashboard-map-infobar-sub">
                      {withinList.length} within {debouncedRadiusKm} km
                    </span>
                  </div>
                  <button type="button" className="crm-dashboard-map-infobar-close"
                    onClick={handleClearSelection} title="Clear selection">×</button>
                </div>
                <div className="crm-dashboard-map-infobar-list">
                  {withinList.length === 0 ? (
                    <div className="crm-dashboard-map-infobar-empty">
                      No other partners within this radius.
                    </div>
                  ) : (
                    withinList.map(p => (
                      <button key={p.id} type="button"
                        className="crm-dashboard-map-infobar-row"
                        onClick={() => handleSelectFromInfobar(p)}
                        title="Select this pin on the map">
                        <span className="crm-dashboard-map-legend-dot"
                          style={{ background: statusOf(p.approval_status).color }} />
                        <span className="crm-dashboard-map-infobar-name">
                          {p.legal_entity_name || 'Unnamed partner'}
                        </span>
                        <span className="crm-dashboard-map-infobar-dist">
                          {p.distanceKm < 1 ? '<1' : Math.round(p.distanceKm)} km
                        </span>
                      </button>
                    ))
                  )}
                </div>
              </div>
            )}

            <div className="crm-dashboard-map-legend">
              {Object.values(STATUS_CONFIG).concat(STATUS_FALLBACK).map(s => (
                <span key={s.label} className="crm-dashboard-map-legend-item">
                  <span className="crm-dashboard-map-legend-dot" style={{ background: s.color }} />
                  {s.label}
                </span>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default CRMDashboardMap;
